import { DateFilterConfig as InstanceDateFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/date-filter-config';
import { FilterConfig as InstanceFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/filters-config';
import { RealtimeFilterConfig as InstanceRealtimeFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/realtime-filter-config';
import { RegularFilterConfig as InstanceRegularFilterConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-config';
import { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config';
import { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance';

import { Route } from 'vue-router';

import { FilterTypeEnum } from '../enums';

import {
  ContextState,
  DateFilter,
  DateFilterConfig,
  DateFilterSelectedValues,
  Dictionary,
  DynamicDateFilterConfig,
  DynamicRealtimeFilterConfig,
  DynamicRegularFilterConfig,
  Filter,
  FilterConfig,
  FilterSelectedValues,
  RealtimeFilter,
  RealtimeFilterConfig,
  RealtimeFilterSelectedValues,
  RegularFilter,
  RegularFilterConfig,
  RegularFilterSelectedValues,
  RouteFiltersConfig
} from '../models';

import { ROUTER_CONSTS } from '../router/router.consts';

export class FiltersUtils {

  public static getFiltersFromContextAndRoute(context: ContextState, route: Route, instanceConfig: Instance): Dictionary<Filter> {
    const filters: Dictionary<Filter> = {};
    const filtersConfig = FiltersUtils.getFiltersConfigFromRoute(route, instanceConfig);
    Object.values(filtersConfig).forEach((filterConfig: FilterConfig) => {
      filters[filterConfig.id] = {
        config: filterConfig,
        selectedValues: FiltersUtils.getFilterSelectedValues(context, route, filterConfig)
      };
    });
    return FiltersUtils.setFiltersToBeValid(filters);
  }

  public static updateRouteFiltersWithContext(route: Route, context: ContextState): Route {
    Object.values(context.filters).forEach((filter: Filter) => {
      const filterUrlValues = {
        [FilterTypeEnum.DATE]: () => FiltersUtils.getDateFilterUrlValues(filter as DateFilter),
        [FilterTypeEnum.REALTIME]: () => FiltersUtils.getRealtimeFilterUrlValues(filter as RealtimeFilter),
        [FilterTypeEnum.REGULAR]: () => FiltersUtils.getRegularFilterUrlValues(filter as RegularFilter)
      }[filter.config.type]();
      const queryFilterId = `${ ROUTER_CONSTS.query.filterPrefix }${ filter.config.id }`;
      if (!!filterUrlValues) {
        route.query[queryFilterId] = filterUrlValues;
      } else {
        delete route.query[queryFilterId];
      }
    });
    return route;
  }

  private static getFiltersConfigFromRoute(route: Route, instanceConfig: Instance): Dictionary<FilterConfig> {
    const filtersConfig: Dictionary<FilterConfig> = {};

    const routeFiltersConfig = (!!route.meta.filters ? route.meta.filters(instanceConfig) : undefined);
    Object.values(instanceConfig.filters).forEach((instanceFilterConfig: InstanceFilterConfig) => {
      const filterConfig = FiltersUtils.mergeInstanceAndRouteFilterConfig(instanceFilterConfig, routeFiltersConfig);
      filtersConfig[filterConfig.id] = filterConfig;
    });

    if (!!routeFiltersConfig && !!routeFiltersConfig.extra) {
      Object.values(routeFiltersConfig.extra).forEach((extraFilterConfig: FilterConfig) => {
        filtersConfig[extraFilterConfig.id] = {
          ...extraFilterConfig,
          enabled: true
        };
      });
    }

    return filtersConfig;
  }

  private static mergeInstanceAndRouteFilterConfig(instanceFilterConfig: InstanceFilterConfig,
    routeFiltersConfig: RouteFiltersConfig): FilterConfig {
    const routeFilterConfig = ((!!routeFiltersConfig && !!routeFiltersConfig.config) ? routeFiltersConfig.config[instanceFilterConfig.id]
      : undefined);
    const filterConfig = {
      [FilterTypeEnum.DATE]: () => FiltersUtils.mergeInstanceAndRouteDateFilterConfig((instanceFilterConfig as InstanceDateFilterConfig),
        (routeFilterConfig as DynamicDateFilterConfig)),
      [FilterTypeEnum.REALTIME]: () => FiltersUtils.mergeInstanceAndRouteRealtimeFilterConfig(
        (instanceFilterConfig as InstanceRealtimeFilterConfig), (routeFilterConfig as DynamicRealtimeFilterConfig)),
      [FilterTypeEnum.REGULAR]: () => FiltersUtils.mergeInstanceAndRouteRegularFilterConfig(
        (instanceFilterConfig as InstanceRegularFilterConfig), (routeFilterConfig as DynamicRegularFilterConfig))
    }[instanceFilterConfig.type]();

    if (filterConfig.type === FilterTypeEnum.REGULAR) {
      const regularFilterConfig = (filterConfig as RegularFilterConfig);
      if (!!routeFiltersConfig) {
        regularFilterConfig.enabled = (
          (filterConfig.enabled !== undefined) ?
            filterConfig.enabled :
            routeFiltersConfig.regularFiltersEnabled
        );
        regularFilterConfig.multiselectDisabled = (
          (regularFilterConfig.multiselectDisabled !== undefined) ?
            regularFilterConfig.multiselectDisabled :
            routeFiltersConfig.multiselectDisabled
        );
      }
      if (regularFilterConfig.enabled && (regularFilterConfig.values.length < 2)) {
        regularFilterConfig.enabled = false;
      }
    }

    return filterConfig;
  }

  private static mergeInstanceAndRouteDateFilterConfig(instanceFilterConfig: InstanceDateFilterConfig,
    routeFilterConfig: DynamicDateFilterConfig): DateFilterConfig {
    let { minDateInMillis, maxDateInMillis, maxPeriodInMillis } = instanceFilterConfig;
    if (!!routeFilterConfig) {
      const routeMinDateInMillis = routeFilterConfig.minDateInMillis;
      if (!!routeMinDateInMillis) {
        minDateInMillis = (!!minDateInMillis ? Math.max(minDateInMillis, routeMinDateInMillis) : routeMinDateInMillis);
      }
      const routeMaxDateInMillis = routeFilterConfig.maxDateInMillis;
      if (!!routeMaxDateInMillis) {
        maxDateInMillis = (!!maxDateInMillis ? Math.min(maxDateInMillis, routeMaxDateInMillis) : routeMaxDateInMillis);
      }
      const routeMaxPeriodInMillis = routeFilterConfig.maxPeriodInMillis;
      if (!!routeMaxPeriodInMillis) {
        maxPeriodInMillis = (!!maxPeriodInMillis ? Math.min(maxPeriodInMillis, routeMaxPeriodInMillis) : routeMaxPeriodInMillis);
      }
    }
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      minDateInMillis,
      maxDateInMillis,
      maxPeriodInMillis
    };
  }

  private static mergeInstanceAndRouteRealtimeFilterConfig(instanceFilterConfig: InstanceRealtimeFilterConfig,
    routeFilterConfig: DynamicRealtimeFilterConfig): RealtimeFilterConfig {
    let intervalsInMinutes = (!!routeFilterConfig ? routeFilterConfig.intervalsInMinutes : []);
    if (!!instanceFilterConfig.maxPeriodInMinutes) {
      intervalsInMinutes = intervalsInMinutes.filter((intervalInMinutes) => (intervalInMinutes <= instanceFilterConfig.maxPeriodInMinutes));
    }
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      intervalsInMinutes
    };
  }

  private static mergeInstanceAndRouteRegularFilterConfig(instanceFilterConfig: InstanceRegularFilterConfig,
    routeFilterConfig: DynamicRegularFilterConfig): RegularFilterConfig {
    const multiselectDisabled = (instanceFilterConfig.multiselectDisabled ||
      (!!routeFilterConfig && routeFilterConfig.multiselectDisabled) || false);
    return {
      ...instanceFilterConfig,
      ...routeFilterConfig,
      multiselectDisabled
    };
  }

  private static getFilterSelectedValues(context: ContextState, route: Route, filterConfig: FilterConfig): FilterSelectedValues {
    const filter = context.filters[filterConfig.id];
    const filterContextValues = (!!filter ? filter.selectedValues : undefined);
    if (!!filterContextValues) {
      return filterContextValues;
    }
    const filterUrlValues = (route.query[`${ ROUTER_CONSTS.query.filterPrefix }${ filterConfig.id }`] as string);
    return {
      [FilterTypeEnum.DATE]: FiltersUtils.getDateFilterSelectedValuesFromUrl,
      [FilterTypeEnum.REALTIME]: FiltersUtils.getRealtimeFilterSelectedValuesFromUrl,
      [FilterTypeEnum.REGULAR]: FiltersUtils.getRegularFilterSelectedValuesFromUrl
    }[filterConfig.type](filterUrlValues);
  }

  private static getDateFilterSelectedValuesFromUrl(filterUrlValues: string): DateFilterSelectedValues {
    const filterValues = ((!!filterUrlValues && filterUrlValues.split(',')) || undefined);
    const startDateInMillis = ((!!filterValues && Date.parse(filterValues[0])) || undefined);
    const endDateInMillis = ((!!filterValues && Date.parse(filterValues[1])) || undefined);
    return [startDateInMillis, endDateInMillis];
  }

  private static getRealtimeFilterSelectedValuesFromUrl(filterUrlValues: string): RealtimeFilterSelectedValues {
    return (!!filterUrlValues ? Number(filterUrlValues) : undefined);
  }

  private static getRegularFilterSelectedValuesFromUrl(filterUrlValues: string): RegularFilterSelectedValues {
    return (!!filterUrlValues ? filterUrlValues.split(',') : []);
  }

  private static setFiltersToBeValid(filters: Dictionary<Filter>): Dictionary<Filter> {
    Object.values(filters).forEach((filter: Filter) => {
      filters[filter.config.id] = {
        [FilterTypeEnum.DATE]: () => FiltersUtils.setDateFilterToBeValid(filter as DateFilter),
        [FilterTypeEnum.REALTIME]: () => FiltersUtils.setRealtimeFilterToBeValid(filter as RealtimeFilter),
        [FilterTypeEnum.REGULAR]: () => FiltersUtils.setRegularFilterToBeValid(filter as RegularFilter)
      }[filter.config.type]();
    });
    return filters;
  }

  private static setDateFilterToBeValid(filter: DateFilter): DateFilter {
    if (filter.config.enabled && filter.config.mandatory && (!filter.selectedValues[0] || !filter.selectedValues[1])) {
      const now = new Date(Date.now());
      const defaultStartDateInMillis = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), (now.getUTCDate() - 7));
      const defaultEndDateInMillis = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
      filter.selectedValues = [defaultStartDateInMillis, defaultEndDateInMillis];
    }
    return filter;
  }

  private static setRealtimeFilterToBeValid(filter: RealtimeFilter): RealtimeFilter {
    if (filter.config.enabled) {
      if (!!filter.selectedValues && !filter.config.intervalsInMinutes.includes(filter.selectedValues)) {
        filter.selectedValues = undefined;
      }
      if (filter.config.mandatory && !filter.selectedValues) {
        filter.selectedValues = filter.config.intervalsInMinutes[0];
      }
    }
    return filter;
  }

  private static setRegularFilterToBeValid(filter: RegularFilter): RegularFilter {
    if (filter.config.enabled) {
      filter.selectedValues = filter.selectedValues.filter((selectedValue: string) =>
        filter.config.values.some((valueConfig: RegularFilterValueConfig) => (valueConfig.id === selectedValue)));
      if (filter.config.multiselectDisabled && (filter.selectedValues.length > 1)) {
        filter.selectedValues = [];
      }
    }
    return filter;
  }

  private static getDateFilterUrlValues(filter: DateFilter): string {
    if (!!filter.selectedValues[0] || !!filter.selectedValues[1]) {
      return filter.selectedValues.map((dateInMillis: number) => {
        if (!!dateInMillis) {
          return `${ new Date(dateInMillis).toISOString().substr(0, 16) }Z`;
        }
      }).join();
    }
  }

  private static getRealtimeFilterUrlValues(filter: RealtimeFilter): string {
    if (!!filter.selectedValues) {
      return filter.selectedValues.toString();
    }
  }

  private static getRegularFilterUrlValues(filter: RegularFilter): string {
    if (!!filter.selectedValues.length) {
      return filter.selectedValues.join();
    }
  }

}
